import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import WeatherPulseNav from "./WeatherPulseNav";
import { ArrowRight, BookOpen, Drop, Thermometer } from "@phosphor-icons/react";

const INTRO = `The Classroom Journal lines up every school station's day side by side — so a class in Pune can see what a class in Portland measured on the very same day. Pick a date and compare.`;

const today = () => new Date().toISOString().slice(0, 10);

export default function SchoolsJournal() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get("/weather-pulse/schools/journal", { params: { date } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ entries: [] }))
      .finally(() => setLoading(false));
  }, [date]);

  const entries = data?.entries || [];
  const reporting = entries.filter((e) => e.reports > 0);

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-schools-journal">
      <div className="max-w-[1000px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2 flex items-center gap-1.5">
          <BookOpen size={13} weight="fill" /> / Schools programme
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.0] tracking-tight">Classroom journal.</h1>
        <p className="mt-3 max-w-2xl text-[#3A3830] text-sm md:text-base">{INTRO}</p>
        <div className="mt-3"><ListenTranslateBar text={INTRO} testidPrefix="wp-journal-tools" /></div>
        <div className="mt-5"><PageGuide pageKey="weather:schools-journal" testidPrefix="wp-schools-journal" /></div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <input
            type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)}
            data-testid="wp-journal-date"
            className="rounded-full border border-black/15 bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#1B4332]"
          />
          <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]/70" data-testid="wp-journal-count">
            {loading ? "Loading…" : `${reporting.length} of ${entries.length} classrooms reported`}
          </span>
        </div>

        <div className="mt-6 space-y-3" data-testid="wp-journal-entries">
          {!loading && entries.length === 0 && (
            <p className="text-sm text-[#3A3830]" data-testid="wp-journal-empty">
              No classroom stations yet — <Link to="/weather-pulse/schools" className="underline underline-offset-2 decoration-[#1B4332]">register your school</Link> to start the journal.
            </p>
          )}
          {entries.map((e) => (
            <Link key={e.station_id} to={`/weather-pulse/station/${e.station_id}`} data-testid={`wp-journal-entry-${e.station_id}`}
              className="group block rounded-2xl bg-white border border-black/10 p-5 hover:border-[#1B4332] hover:-translate-y-0.5 transition-all">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-serif-display text-xl leading-tight">{e.school}</div>
                  <div className="mt-0.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/70">
                    {[e.city, e.country].filter(Boolean).join(", ")}
                    {e.grade_levels.length > 0 && <> · Grades {e.grade_levels.join(", ")}</>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {e.reports > 0 ? (
                    <>
                      {e.temp_avg != null && (
                        <div className="text-center">
                          <div className="inline-flex items-center gap-1 font-serif-display text-2xl text-[#1B4332]">
                            <Thermometer size={15} weight="fill" /> {e.temp_avg}°
                          </div>
                          <div className="font-mono-tech text-[8px] tracking-[0.18em] uppercase text-[#3A3830]/60">
                            {e.temp_min}° – {e.temp_max}°
                          </div>
                        </div>
                      )}
                      <div className="text-center">
                        <div className="inline-flex items-center gap-1 font-serif-display text-2xl text-[#0C4A6E]">
                          <Drop size={15} weight="fill" /> {e.rainfall_mm}
                        </div>
                        <div className="font-mono-tech text-[8px] tracking-[0.18em] uppercase text-[#3A3830]/60">mm rain</div>
                      </div>
                      <div className="text-center">
                        <div className="font-serif-display text-2xl">{e.reports}</div>
                        <div className="font-mono-tech text-[8px] tracking-[0.18em] uppercase text-[#3A3830]/60">reports</div>
                      </div>
                    </>
                  ) : (
                    <span className="rounded-full border border-black/10 border-dashed px-3 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#3A3830]/50">
                      Quiet this day
                    </span>
                  )}
                  <ArrowRight size={13} weight="bold" className="text-[#1B4332] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              {e.conditions.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {e.conditions.map((c) => (
                    <span key={c.condition} className="rounded-full bg-[#1B4332]/8 border border-[#1B4332]/15 px-2.5 py-0.5 font-mono-tech text-[8px] tracking-[0.16em] uppercase text-[#1B4332]">
                      {c.condition.replace("-", " ")} ×{c.count}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-black/10 bg-white p-5 text-sm text-[#3A3830]">
          Teachers: tag each report with your classroom station when you <Link to="/weather-pulse" className="underline underline-offset-2 decoration-[#1B4332]">report the weather</Link> and it appears here automatically — temperature, rainfall and conditions, side by side with every other school.
        </div>
      </div>
    </main>
  );
}
